<template>
	<simple-input ref="numeric" v-bind="{...$attrs, ...$props}" :value="visibleValue" type="tel" @blur="onBlurHandler" @keydown="$emit('keydown', $event)"
		@update:value="onInputHandler" @focus="onFocusHandler" @clear="$emit('clear')">
		<span slot="suffix">
			<slot name="suffix" />
		</span>
		<template slot="prepend">
			<dms-button v-if="!isNaN(step)" :wrapp="false" :disabled="disabled || isDisabled" icon="el-icon-minus" @click="decrease" />
			<slot name="prepend" />
		</template>
		<template slot="append">
			<slot name="append" />
			<dms-button v-if="!isNaN(step)" :wrapp="false" :disabled="disabled || isDisabled" icon="el-icon-plus" @click="increase" />
		</template>
	</simple-input>
</template>

<script>
import accounting from 'accounting';
import _ from 'lodash';

import SimpleInput from './SimpleInput.vue';

const max = Number.MAX_SAFE_INTEGER;
const min = Number.MIN_SAFE_INTEGER;

export default SimpleInput.extend({
	name: 'DmsInputNumber',
	components: { SimpleInput },
	props: {
		disabled: {
			type: Boolean,
			default: undefined,
		},
		/**
		 *  Add + - buttons with event for increase
		 */
		step: {
			type: Number,
		},
		/**
		 * default val
		 */
		defaultValue: {
			type: Number,
		},
		/**
		 * Enable/Disable minus value.
		 */
		minus: {
			type: Boolean,
			default: true,
		},

		/**
		 * Number of decimals.
		 * Decimals symbol are the opposite of separator symbol.
		 */
		precision: {
			type: Number,
		},

		/**
		 * Forced thousand separator.
		 * Accepts any string.
		 */
		thousandSeparator: {
			default: undefined,
			type: String,
		},

		/**
		 * Forced decimal separator.
		 * Accepts any string.
		 */
		decimalSeparator: {
			default: ',',
			type: String,
		},

		/**
		 * Forced decimal separator.
		 * Accepts any string.
		 */
		 decimalSeparatorIndiaMarket: {
			default: '.',
			type: String,
		},

		/**
		 * v-model value.
		 */
		value: {
			required: true,
			validator: (prop) => {
				if (typeof prop !== 'number' && prop !== null) {
					console.error(`Value of InputNumber is of wrong type: ${prop}`);
					return false;
				}
				return true;
			},
		},

		/**
		 * Currency symbol.
		 */
		currency: {
			type: String,
			default: '',
		},

		span: {
			type: Number,
			default: 24,
		},

		/**
		 * Position of currency symbol
		 * Symbol position props accept either 'suffix' or 'prefix' (default).
		 */
		currencySymbolPosition: {
			type: String,
			default: 'prefix',
		},
	},

	data: () => ({
		visibleValue: '',
	}),

	computed: {
		/**
		 * Number type of value props.
		 * @return {Number}
		 */
		valueNumber() {
			return this.unformat(this.value);
		},

		/**
		 * Define decimal separator based on separator props.
		 * @return {String} '.' or ','
		 */
		decimalSeparatorSymbol() {
			if (this.dmsSys.IndiaCFDPrp) {
				if (typeof this.decimalSeparatorIndiaMarket !== 'undefined') return this.decimalSeparatorIndiaMarket;
				return '.';
			}

			if (typeof this.decimalSeparator !== 'undefined') return this.decimalSeparator;
			return ',';
		},

		/**
		 * Define thousand separator based on separator props.
		 * @return {String} '.' or ','
		 */
		thousandSeparatorSymbol() {
			if (typeof this.thousandSeparator !== 'undefined') return this.thousandSeparator;
			return '';
		},

		/**
		 * Define format position for currency symbol and value.
		 * @return {String} format
		 */
		symbolPosition() {
			if (!this.currency) return '%v';
			return this.currencySymbolPosition === 'suffix' ? '%v %s' : '%s %v';
		},
		precisionCmp() {
			return this.precision || this.getNumberRule('scale') || 0;
		},
	},

	watch: {
		/**
		 * Watch for value change from other input with same v-model.
		 * @param {Number} newValue
		 */
		valueNumber(newValue) {
			if (this.$refs.numeric.$refs.elInput.$refs.input !== document.activeElement) {
				this.visibleValue = this.format(newValue);
			}
		},

		/**
		 * Immediately reflect precision changes
		 */
		precisionCmp() {
			this.process(this.valueNumber);
			this.visibleValue = this.format(this.valueNumber);
		},
	},
	beforeCreate() {
		if (typeof this.$options.propsData.value === 'undefined') {
			console.error('Value undefined in input-number: ', this.$options.propsData.labelKey);
		}
	},
	mounted() {
		if (!this.isEmpty(this.valueNumber)) this.visibleValue = this.format(this.valueNumber);
		this.formItem = this.$refs.numeric.formItem;
	},

	methods: {
		/**
		 * Handle increase button.
		 */
		increase() {
			this.$emit('increase');
			if (this.step) {
				this.update(this.value + this.step);
			}
		},
		/**
		 * Handle increase button.
		 */
		decrease() {
			this.$emit('decrease');
			if (this.step) {
				this.update(this.value - this.step);
			}
		},
		/**
		 * Handle blur event.
		 * @param {Object} e
		 */
		onBlurHandler(e) {
			this.$emit('blur', e);
			this.visibleValue = this.format(this.valueNumber);
		},

		/**
		 * Handle focus event.
		 * @param {Object} e
		 */
		onFocusHandler(e) {
			this.$emit('focus', { event: e, vueElement: this });
			if (this.isEmpty(this.valueNumber)) {
				this.visibleValue = null;
			} else {
				this.formatMoney(this.valueNumber, true);
			}
		},

		/**
		 * Handle input event.
		 */
		onInputHandler(value) {
			if ((value === '' || value === null || value === undefined) && _.isNumber(this.defaultValue)) {
				this.visibleValue = this.defaultValue;
				this.process(this.unformat(this.defaultValue));
			} else {
				const replace = `[^0-9${this.minus ? '-' : ''}${this.decimalSeparatorSymbol}${this.thousandSeparatorSymbol}]`;
				const re = new RegExp(replace, 'g');
				this.visibleValue = _.isNil(value) ? value : value.replace(re, '');
				this.process(this.unformat(this.visibleValue));
			}
		},

		/**
		 * Validate value before update the component.
		 * @param {Number} value
		 */
		process(value) {
			if (value >= max) {
				this.update(max);
			}
			if (value <= min) {
				this.update(min);
			}
			if (value > min && value < max) {
				this.update(value);
			}
			if (!this.minus && value < 0) {
				this.update(Math.max(min, 0));
			}
		},

		/**
		 * Update parent component model value.
		 * @param {Number} value
		 */
		update(value) {
			let output = value;
			if (!this.isEmpty(value)) {
				const fixedValue = accounting.toFixed(value, this.precisionCmp);
				output = Number(fixedValue);
			}
			this.$emit('update:value', output);
		},

		/**
		 * Format value using symbol and separator.
		 * @param {Number} value
		 * @return {String}
		 */
		format(value) {
			return (!this.isEmpty(value)) ? this.formatMoney(value) : null;
		},

		formatMoney(value, forTyping) {
			const option = {
				symbol: forTyping ? '' : this.currency,
				format: forTyping ? '%v' : this.symbolPosition,
				precision: this.precisionCmp,
				decimal: this.decimalSeparatorSymbol,
				thousand: forTyping ? '' : this.thousandSeparatorSymbol,
			};
			return accounting.formatMoney(value, option);
		},

		/**
		 * Remove symbol and separator.
		 * @param {Number} value
		 * @return {Number}
		 */
		unformat(value) {
			if (this.isEmpty(value)) return null;
			const toUnformat = typeof value === 'string' && value === '' ? this.emptyValue : value;
			return !this.isEmpty(toUnformat) ? accounting.unformat(toUnformat, this.decimalSeparatorSymbol) : null;
		},

		isEmpty(value) {
			return value === null || value === '';
		},
	},
});
</script>
